import {
  AccountDetails,
  EventType,
  PlanPackageDetails,
  SimplePlanPackageDetails,
  SubscriptionDetails,
} from "features/types";
import intersection from "lodash/intersection";
import uniqBy from "lodash/uniqBy";
import { useMemo } from "react";
import { getPlanType } from "utils/planPackageUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { useAccount } from "./useAccount";
import { useAppSelector } from "./useAppSelector";

const differenceOfTwoArrays = (arr1: any[], arr2: any[]) => {
  return arr1
    .filter((x) => !arr2.includes(x))
    .concat(arr2.filter((x) => !arr1.includes(x)));
};

export const getUniqAccountSubscriptions = (account: AccountDetails) => {
  const validAccountSubscriptions = account.subscriptions.filter(
    (sub) =>
      filterIneligibleSubscriptions(sub) &&
      getPlanType(sub.planPackage) === "RESERVATION"
  );
  const uniqValidAccountSubscriptions = uniqBy(
    validAccountSubscriptions,
    "planPackage.id"
  );
  return uniqValidAccountSubscriptions;
};

export const getAccountSubscriptionEventTypes = (
  account: AccountDetails
): EventType[] => {
  const uniqAccountSubscriptions = getUniqAccountSubscriptions(account);
  return uniqAccountSubscriptions.reduce((eventTypes, currentSubscription) => {
    const newEventTypes = currentSubscription.expired
      ? []
      : currentSubscription.planPackage.eventTypes;
    return eventTypes.concat(newEventTypes);
  }, [] as EventType[]);
};

export const findUpgradablePlans = (
  planPackages: PlanPackageDetails[],
  accountSubscriptionEventTypes: EventType[],
  selectedPlanEventTypes: EventType[],
  selectedPlanPackage: SimplePlanPackageDetails
) => {
  // At this step, we know that selectedPlanPackage cannot be a Voucher plan
  // check https://github.com/FanRally/fanrally-core/issues/604 for reference
  const filteredPlans = planPackages.filter((plan) => {
    if (getPlanType(selectedPlanPackage) !== getPlanType(plan)) return false; // cannot upgrade reservation plan to bank, or vice versa
    if (intersection(plan.eventTypes, selectedPlanEventTypes).length > 0) {
      const differenceOfTwoEventTypes = differenceOfTwoArrays(
        plan.eventTypes,
        selectedPlanEventTypes
      );
      if (
        intersection(accountSubscriptionEventTypes, differenceOfTwoEventTypes)
          .length > 0
      ) {
        return false;
      }
      return Number(plan.fullPrice) > Number(selectedPlanPackage.fullPrice);
    }
    return false;
  });
  return filteredPlans;
};

/**
 * This hook returns the upgradable plans for a specific subscription
 * @param selectedSubscription
 * @returns
 */
const useUpgradablePlansForSubscription = (
  selectedSubscription: SubscriptionDetails | undefined
) => {
  const { account } = useAccount();
  const { upgradablePlans } = useAppSelector((state) => state.buyFlow);
  const accountSubscriptionEventTypes = useMemo(() => {
    if (!account) return [];
    return getAccountSubscriptionEventTypes(account);
  }, [account]);

  const upgradablePlansForSelectedSubscription = useMemo(() => {
    if (!selectedSubscription) return [];
    return findUpgradablePlans(
      upgradablePlans || [],
      accountSubscriptionEventTypes,
      selectedSubscription.planPackage!.eventTypes,
      selectedSubscription.planPackage!
    );
  }, [upgradablePlans, accountSubscriptionEventTypes, selectedSubscription]);

  return upgradablePlansForSelectedSubscription;
};

export default useUpgradablePlansForSubscription;
